import type { FC, SVGProps } from "react"
import React, { memo } from "react"

import type { Phase } from "@logic/lib/design"
import { PHASES } from "@logic/lib/design"
import { AnimatePresence, motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { selectorFamily, useRecoilState, useRecoilValue } from "recoil"
import styled, { css } from "styled-components"

import { pauseDesignRequestModalIsOpenState } from "~/containers/Proposal/state/design-request/use-design-request"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { Gap, ModalUnderlay } from "~/containers/proposal-styles"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import { fontWeight } from "~/utils/styles"

import { ReactComponent as CaretIcon } from "@svg/arrow-down.svg"
import { ReactComponent as Checkmark } from "@svg/phase/checkmark.svg"
import { ReactComponent as CircleMinusExclam } from "@svg/phase/circle-minus-exclam.svg"

import { PauseRequestModal } from "./PauseRequestModal"
import { useDesignerCapabilities } from "../designer-capabilities"
import { PhaseOptionsFor } from "./PhaseOptions"
import { useFocusedFlaws } from "../state/flaws"
import {
  areWeAfterPhase,
  mayEnterNextPhaseState,
  nextPhaseBlockersState,
  phaseDescriptionFor,
  phaseNameFor,
  phaseState,
  useAttemptPhaseTransition,
  userHasRequiredPermissionsForPhaseState,
} from "../state/phase"

export type PhaseCompleteness = `blocked` | `future` | `past` | `unblocked`

const CompletenessIconFor: Record<
  PhaseCompleteness,
  FC<SVGProps<SVGElement>>
> = {
  blocked: CircleMinusExclam,
  future: () => null,
  past: Checkmark,
  unblocked: () => null,
}

const findPhaseCompletenessState = selectorFamily<PhaseCompleteness, Phase>({
  key: `phaseCompleteness`,
  get:
    (phase) =>
    ({ get }) => {
      const { phase: currentPhase } = get(designLocalState)
      if (currentPhase === phase) {
        return get(mayEnterNextPhaseState) ? `unblocked` : `blocked`
      } else if (get(areWeAfterPhase(phase))) {
        return `past`
      }

      return `future`
    },
})

const ListItem = styled(motion.li)<{
  status: PhaseCompleteness
  isCurrentPhase: boolean
}>`
  display: flex;
  flex-direction: column;
  padding: 10px var(--space-s);
  gap: var(--space-s);
  background-color: var(--white);
  border-bottom: 1px solid #dddddd;
  button[data-role="phase-title"] {
    font-weight: ${({ isCurrentPhase }) => (isCurrentPhase ? 600 : 500)};
    display: flex;
    gap: 10px;
    text-align: left;
    font-size: var(--step--1);
    line-height: 21px;
    text-transform: capitalize;
    cursor: pointer;
    color: ${({ isCurrentPhase }) =>
      isCurrentPhase ? `var(--blue-dark-font)` : `var(--grey-light-font)`};
    transition: color 0.15s ease;
    :hover {
      color: var(--blue-dark-font);
    }
    &:disabled {
      cursor: not-allowed;
      :hover {
        color: var(--grey-light-font);
      }
    }
    ${({ status }) =>
      status === `blocked` &&
      css`
        color: var(--grey-dark);
        cursor: not-allowed;
        &:hover {
          color: var(--grey-dark);
        }
      `}
    svg {
      &[data-role="caret"] {
        width: 10px;
        height: 10px;
        transition: all 0.3s ease;
        transform: rotate(-90deg) translateX(2px);
        path {
          fill: var(--grey-light-font);
        }
        ${({ status }) =>
          [`blocked`, `unblocked`].includes(status) &&
          css`
            ${fontWeight(600)}
            color: var(--blue);
            transform: rotate(0deg) translateX(2px) translateY(-2px);
            transition: transform 0.3s ease;
          `}
      }
      &[data-role="completeness-indicator"] {
        width: 18px;
        height: 18px;
        margin: 0px 4px;
      }
    }
  }
  [data-role="details"] {
    display: flex;
    flex-direction: column;
    font-size: 16px;
  }
  [data-role="blockers"] {
    display: flex;
    flex-direction: column;
    gap: 10px;
    ${({ status }) =>
      status === `blocked` &&
      css`
        background-color: #d89c0033;
        border-radius: 4px;
        padding: 10px;
        mix-blend-mode: multiply;
      `}

    h2 {
      ${fontWeight(500)}
      font-size: 14px;
    }
    [data-role="offenders"] {
      padding-left: 24px;
      li {
        list-style-type: disc;
        > * {
          font-size: 14px;
        }
        &[data-state="held-focused"] > * {
          ${fontWeight(600)}
          background-color: var(--danger);
          color: var(--white);
          cursor: pointer;
          &:active {
            outline: 2px solid var(--danger);
          }
        }
      }
    }
  }
`

const ActionButton = styled(motion.button)<{ preventedForUser?: boolean }>`
  height: 33px;
  background: var(--blue);
  color: var(--white);
  border-radius: 4px;
  font-size: var(--step--2);
  letter-spacing: 0.5px;
  font-weight: 500;
  line-height: 17px;
  cursor: pointer;
  text-transform: capitalize;
  &:hover:not([disabled]) {
    background: var(--blue-dark);
  }
  &:disabled {
    background: var(--grey-border);
    cursor: not-allowed;
    mix-blend-mode: multiply;
  }
  ${({ preventedForUser }) =>
    preventedForUser &&
    css`
      background: var(--grey-border);
      cursor: not-allowed;
      mix-blend-mode: multiply;

      &:hover:not([disabled]) {
        background: var(--grey-border);
      }
    `}
`

const PhaseListItemTitle_Internal: FC<{
  children?: React.ReactNode
  phase: Phase
  isCurrentPhase: boolean
}> = ({ children, phase, isCurrentPhase }) => {
  const { t } = useTranslation()
  const isLastPhase = phase === PHASES[PHASES.length - 1]
  const phaseCompleteness = useRecoilValue(findPhaseCompletenessState(phase))
  const CompletenessIcon = isLastPhase
    ? () => null
    : CompletenessIconFor[phaseCompleteness]
  const hasRequiredPermissions = useRecoilValue(
    userHasRequiredPermissionsForPhaseState(phase),
  )
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()
  const shouldDisable = phaseCompleteness === `future` || !hasRequiredPermissions
  const attemptPhaseTransition = useAttemptPhaseTransition()

  return (
    <ListItem
      layout
      status={!currentUserIsDesignUser ? `blocked` : phaseCompleteness}
      isCurrentPhase={isCurrentPhase}
    >
      <motion.button
        layout
        data-role="phase-title"
        disabled={shouldDisable}
        onClick={
          !currentUserIsDesignUser
            ? triggerPreventionToast
            : () => attemptPhaseTransition(phase)
        }
      >
        <span>
          <CaretIcon data-role="caret" />
        </span>
        <>{t(phaseNameFor[phase])}</>
        <Gap />
        <span>
          <CompletenessIcon data-role="completeness-indicator" />
        </span>
      </motion.button>
      {children}
    </ListItem>
  )
}

export const PhaseListItemTitle = memo(PhaseListItemTitle_Internal)

const PhaseListItem_Internal: FC<{
  phase: Phase
  continueOrComplete: ({ completeAndNext }: { completeAndNext: boolean }) => void
  isCurrentPhase: boolean
}> = ({ phase, continueOrComplete, isCurrentPhase }) => {
  const { t } = useTranslation()
  const isLastPhase = phase === PHASES[PHASES.length - 1]
  const nextPhaseBlockers = useRecoilValue(nextPhaseBlockersState)
  const mayEnterNextPhase = useRecoilValue(mayEnterNextPhaseState)
  const [pauseRequestModalIsOpen, setPauseRequestModalIsOpen] = useRecoilState(
    pauseDesignRequestModalIsOpenState,
  )
  const focusedFlaws = useFocusedFlaws()
  const PhaseOptions = PhaseOptionsFor[phase]
  const hasRequiredPermissions = useRecoilValue(
    userHasRequiredPermissionsForPhaseState(phase),
  )
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()
  const { designRequest: hasDesignRequest } = useDesignerCapabilities()
  const isServiceBureauRoute =
    hasDesignRequest &&
    window.location.pathname.includes(`designer/service-queue`)
  const isPanelPlacementPhase = phase === `Panel Placement`

  const clickContinueOrComplete: ({
    completeAndNext,
  }: {
    completeAndNext: boolean
  }) => void = ({ completeAndNext }) => {
    if (!currentUserIsDesignUser) {
      triggerPreventionToast()

      return
    }
    continueOrComplete({ completeAndNext })
  }

  return (
    <>
      {isServiceBureauRoute && pauseRequestModalIsOpen && (
        <>
          <ModalUnderlay
            onClick={() => setPauseRequestModalIsOpen(false)}
            isInUse={pauseRequestModalIsOpen}
            style={{
              backdropFilter: `blur(5px)`,
              backgroundColor: `#0002`,
              opacity: 0.5,
            }}
          />
          <PauseRequestModal
            setPauseRequestModalIsOpen={setPauseRequestModalIsOpen}
          />
        </>
      )}
      <PhaseListItemTitle isCurrentPhase={isCurrentPhase} phase={phase}>
        {isCurrentPhase && (
          <>
            <motion.p layout data-role="details">
              <>{t(phaseDescriptionFor[phase])}</>
            </motion.p>
            <PhaseOptions />
          </>
        )}
        {isCurrentPhase && !isLastPhase && (
          <motion.div layout data-role="blockers">
            {nextPhaseBlockers.length === 0
              ? null
              : nextPhaseBlockers.map((blocker) => (
                  <AnimatePresence key={blocker.message}>
                    <motion.div
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.h2 layout>
                        <>{t(blocker.message)}</>
                      </motion.h2>
                      <motion.ul layout data-role="offenders">
                        {blocker.offenders.map((offender) => (
                          <motion.li
                            layout
                            data-role="offender"
                            data-state={
                              focusedFlaws.ids.includes(offender)
                                ? focusedFlaws.isHeld
                                  ? `held-focused`
                                  : `focused`
                                : undefined
                            }
                            key={offender}
                          >
                            <span
                              onMouseEnter={() =>
                                focusedFlaws.setSoft([offender])
                              }
                              onMouseLeave={() => focusedFlaws.setSoft([])}
                              onClick={() =>
                                focusedFlaws.ids.every(
                                  (id) => id === offender,
                                ) && focusedFlaws.isHeld
                                  ? focusedFlaws.resetHard()
                                  : focusedFlaws.setHard([offender])
                              }
                            >
                              {offender}
                            </span>
                          </motion.li>
                        ))}
                      </motion.ul>
                    </motion.div>
                  </AnimatePresence>
                ))}

            <ActionButton
              layout
              data-role="next-phase"
              onClick={() => clickContinueOrComplete({ completeAndNext: false })}
              disabled={currentUserIsDesignUser ? !mayEnterNextPhase : false}
              preventedForUser={!currentUserIsDesignUser}
            >
              <>{t(phase === `Panel Placement` ? `complete` : `continue`)}</>
            </ActionButton>

            {isServiceBureauRoute && currentUserIsDesignUser && (
              <ActionButton
                layout
                style={{ minWidth: 150 }}
                onClick={() => {
                  setPauseRequestModalIsOpen(!pauseRequestModalIsOpen)
                }}
              >
                {t(`needsMoreFeedback`)}
              </ActionButton>
            )}

            {hasRequiredPermissions &&
              isServiceBureauRoute &&
              isPanelPlacementPhase && (
                <ActionButton
                  layout
                  data-role="next-phase"
                  onClick={() =>
                    clickContinueOrComplete({ completeAndNext: true })
                  }
                  disabled={currentUserIsDesignUser ? !mayEnterNextPhase : false}
                  preventedForUser={!currentUserIsDesignUser}
                >
                  {t(`completeAndNext`)}
                </ActionButton>
              )}
          </motion.div>
        )}
      </PhaseListItemTitle>
    </>
  )
}

const PhaseListItem = memo(PhaseListItem_Internal)

const PhaseList_Internal: FC<{
  continueOrComplete: ({ completeAndNext }: { completeAndNext: boolean }) => void
}> = ({ continueOrComplete }) => {
  const currentPhase = useRecoilValue(phaseState)

  return (
    <motion.ol layout>
      {PHASES.map((phase) => {
        const isCurrentPhase = currentPhase === phase

        return isCurrentPhase ? (
          <PhaseListItem
            key={phase + `-info`}
            phase={phase}
            continueOrComplete={continueOrComplete}
            isCurrentPhase={isCurrentPhase}
          />
        ) : (
          <PhaseListItemTitle
            isCurrentPhase={isCurrentPhase}
            key={phase + `-info-title`}
            phase={phase}
          />
        )
      })}
    </motion.ol>
  )
}

export const PhaseList = memo(PhaseList_Internal)
